import { useEffect, useRef, useState } from "react";
import type { ConcursCard } from "../../types";
import { LinkButton } from "../ui/Button";
import { StarMark } from "../ui/StarMark";
import "./ConcursScroll.css";
import { assetUrl } from "../../wordpress";

type ConcursScrollProps = {
	cards: ConcursCard[];
	title?: string;
	showText?: boolean;
};

/**
 * Entra em cena uma única vez quando o mosaico se aproxima da tela; não
 * acompanha o scroll depois disso, então não há cálculo contínuo para
 * quebrar em diferentes ambientes.
 */
function useRevealOnce<T extends HTMLElement>() {
	const ref = useRef<T | null>(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const skipsAnimation =
			typeof IntersectionObserver === "undefined" ||
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (skipsAnimation) {
			setVisible(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.2 },
		);
		observer.observe(element);
		return () => observer.disconnect();
	}, []);

	return { ref, visible };
}

/**
 * Na portada, mantém o mosaico gráfico animado. Na página do concurso, mostra
 * a transcrição em HTML sem depender da animação para permanecer visível.
 */
export function ConcursScroll({
	cards,
	title = 'I Concurs de relats "Blau de vidre"',
	showText = false,
}: ConcursScrollProps) {
	const { ref: gridRef, visible } = useRevealOnce<HTMLDivElement>();

	if (cards.length === 0) return null;

	return (
		<section className="concurs" id="concurs" aria-labelledby="concurs-title">
			<div className="concurs__stage">
				<h2 className="concurs__title" id="concurs-title">
					<StarMark className="concurs__star" />
					{title}
				</h2>

				{showText ? (
					<div className="concurs__text-grid">
						{cards.map((card) => (
							<article className="concurs__text-block" key={card.id}>
								<p className="concurs__kicker">{card.kicker}</p>
								<h3>{card.title}</h3>
								<p>{card.description}</p>
							</article>
						))}
					</div>
				) : (
					<div
						className={visible ? "concurs__grid is-visible" : "concurs__grid"}
						ref={gridRef}
					>
						{cards.map((card) => (
							<figure className="concurs__figure" key={card.id}>
								<img
									src={assetUrl(card.image)}
									alt={`${card.kicker}: ${card.title}. ${card.description}`}
									draggable={false}
								/>
							</figure>
						))}
					</div>
				)}

				<LinkButton
					className="concurs__bases"
					href="https://cosanova.cat/wp-content/uploads/2026/06/Bases.pdf"
					variant="outline"
					target="_blank"
					rel="noreferrer"
				>
					Descarrega les bases completes aquí
				</LinkButton>
			</div>
		</section>
	);
}
